// Lazy Paddle.js bootstrap. Mirrors auth/firebase.ts: the SDK is only imported
// when billing mode is 'paddle', keeping it out of the mock-mode bundle path.

import type { Paddle } from '@paddle/paddle-js';

/** One price id's real, localized, tax-inclusive total from Paddle. */
export interface RealPrice {
  /** Ready-to-display, e.g. "€69.99". */
  formatted: string;
  /** Numeric total, for savings math. */
  amount: number;
  currencyCode: string;
}

/** Window event dispatched after a successful Paddle checkout. */
export const PADDLE_COMPLETE_EVENT = 'babysteps:checkout-complete';

let _paddle: Paddle | null = null;
let _loadPromise: Promise<Paddle> | null = null;

export async function getPaddle(): Promise<Paddle> {
  if (_paddle) return _paddle;
  if (_loadPromise) return _loadPromise;

  _loadPromise = (async () => {
    const token = import.meta.env.VITE_PADDLE_TOKEN;
    if (!token) {
      throw new Error(
        'Paddle billing selected but VITE_PADDLE_TOKEN is missing. ' +
          'Set it in frontend/.env.local or switch VITE_BILLING_MODE=mock.',
      );
    }
    const { initializePaddle } = await import('@paddle/paddle-js');
    const environment =
      (import.meta.env.VITE_PADDLE_ENVIRONMENT as 'sandbox' | 'production' | undefined) ?? 'sandbox';
    const paddle = await initializePaddle({
      token,
      environment,
      // A completed purchase notifies RevenueCat (which webhooks our backend).
      // Locally we also nudge the UI to refetch /me once the cache catches up.
      eventCallback: (ev) => {
        if (ev?.name === 'checkout.completed' && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent(PADDLE_COMPLETE_EVENT));
        }
      },
    });
    if (!paddle) throw new Error('Paddle failed to initialise.');
    _paddle = paddle;
    return paddle;
  })();

  return _loadPromise;
}

/**
 * Real per-price-id pricing from Paddle, keyed by price id — the source of
 * truth for displayed prices. TIER_META's static amounts (data/entitlements.ts)
 * are indicative headline estimates only; Paddle determines the buyer's actual
 * localized, tax-inclusive total at checkout, which routinely differs from
 * them, so anything shown before checkout should come from here when possible.
 * Returns an empty map (never throws) if Paddle/pricing is unavailable, so
 * callers can fall back to the static estimate.
 */
export async function fetchRealPrices(priceIds: string[]): Promise<Record<string, RealPrice>> {
  const ids = priceIds.filter((id): id is string => Boolean(id));
  if (ids.length === 0) return {};
  try {
    const paddle = await getPaddle();
    const preview = await paddle.PricePreview({
      items: ids.map((priceId) => ({ priceId, quantity: 1 })),
    });
    const out: Record<string, RealPrice> = {};
    for (const item of preview.data.details.lineItems) {
      out[item.price.id] = {
        formatted: item.formattedTotals.total,
        amount: Number(item.totals.total),
        currencyCode: preview.data.currencyCode,
      };
    }
    return out;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[billing] Paddle price preview failed', e);
    return {};
  }
}
